import { ROLE_LABELS, ROLE_COLORS } from "@/lib/rbac";
import { linesForUser } from "@/hooks/useOperatorScope";
import { ImportExportButtons } from "@/components/ImportExportButtons";
import { Button } from "@/components/ui/button";
import { Users, Plus, Trash2, Pencil, UserX, UserCheck } from "lucide-react";

function LinesCell({ user: u, machines }) {
  if (u.role !== "operator") return <td className="px-4 py-3 text-xs text-slate-300">—</td>;
  const lines = linesForUser(machines, u.id);
  return (
    <td className="px-4 py-3" data-testid={`user-lines-${u.username}`}>
      {lines.length === 0 ? (
        <span className="text-[11px] text-amber-600 italic">Not assigned</span>
      ) : (
        <span className="flex flex-wrap gap-1">
          {lines.map((m) => <span key={m.id} className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100" title={m.name}>{m.line_code}</span>)}
        </span>
      )}
    </td>
  );
}

function RowActions({ user: u, self, active, onEdit, onToggleActive, onDelete }) {
  return (
    <div className="flex items-center justify-end gap-1">
      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onEdit(u)} data-testid={`edit-user-${u.username}`}>
        <Pencil className="h-3 w-3 mr-1" /> Edit
      </Button>
      {self ? (
        <span className="text-[10px] text-slate-300 italic ml-1">you</span>
      ) : (
        <>
          <Button size="sm" variant="outline" className={`h-7 text-[11px] ${active ? "text-amber-600 border-amber-200" : "text-emerald-600 border-emerald-200"}`}
            onClick={() => onToggleActive(u, !active)} data-testid={`toggle-active-${u.username}`}>
            {active ? <><UserX className="h-3 w-3 mr-1" /> Deactivate</> : <><UserCheck className="h-3 w-3 mr-1" /> Activate</>}
          </Button>
          <button onClick={() => onDelete(u)} className="text-slate-400 hover:text-red-600 p-1" title="Delete" data-testid={`delete-user-${u.username}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  );
}

export default function UserTable({ users, machines = [], currentUser, isAdmin, onAdd, onEdit, onToggleActive, onDelete, onImported }) {
  return (
    <div className="xl:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="rbac-user-management-table">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" />
          <div>
            <h3 className="font-display text-base font-semibold text-slate-900">User Accounts</h3>
            <p className="text-xs text-slate-400">Admin-only account management</p>
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-1.5">
            <ImportExportButtons entity="users" label="users" onImported={onImported} />
            <Button size="sm" onClick={onAdd} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-add-user">
              <Plus className="h-4 w-4 mr-1" /> Add User
            </Button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Username</th>
              <th className="px-4 py-3 font-semibold">Role</th>
              <th className="px-4 py-3 font-semibold">Lines</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Last Login</th>
              {isAdmin && <th className="px-4 py-3 font-semibold text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const active = u.active !== false;
              return (
                <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid="user-row">
                  <td className="px-4 py-3 font-medium text-slate-800">{u.name}</td>
                  <td className="px-4 py-3 text-slate-500 font-mono-data text-xs" data-testid={`user-username-${u.username}`}>@{u.username}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-mono-data uppercase px-2 py-1 rounded ${ROLE_COLORS[u.role]}`}>{ROLE_LABELS[u.role]}</span></td>
                  <LinesCell user={u} machines={machines} />
                  <td className="px-4 py-3"><span className={`text-xs font-medium ${active ? "text-emerald-600" : "text-red-500"}`}>{active ? "Active" : "Deactivated"}</span></td>
                  <td className="px-4 py-3 text-xs text-slate-500" data-testid={`last-login-${u.username}`}>{u.last_login ? new Date(u.last_login).toLocaleString("en-IN") : "—"}</td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <RowActions user={u} self={u.id === currentUser.id} active={active} onEdit={onEdit} onToggleActive={onToggleActive} onDelete={onDelete} />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
